import React, { useState } from 'react';
import { useSelector } from 'react-redux';
import { Collapse } from 'reactstrap';
import styled from 'styled-components';

import { CenterContainer, CustomCardBody, ExtButton } from '../../../styledComponents/styledComponents';

import BlockSpinner from '../../common/BlockSpinner/BlockSpinner';

import { PropTypes } from 'prop-types';
import momentPropTypes from 'react-moment-proptypes';
import DateTimePicker from '../../common/DateTimePicker/DateTimePicker';
import { MAX_DURATION_PER_VIEW } from '../../../constants/timeConstraints';
import { GUARDIAN_VIEW } from '../../../constants/general';
import { clear, isTokenStillValid } from '../../../misc/BrowserStorageManager';
import { history } from '../../../redux/store/configureStore';
import { hashSelector, cancelTokenSelector } from '../../../redux/selectors/selectors';
import moment from 'moment';

import FilterTabBar from './filters/FilterTabBar';
import FilterInputPanel from './filters/FilterInputPanel';
import FilterChipStrip from './filters/FilterChipStrip';
import { Description, DescriptionSpan } from '../../../styledComponents/styledComponents';

// ─── Section layout primitives ───────────────────────────────────────────────

const SectionLabel = styled.div`
   font-size: 10px;
   font-weight: 600;
   color: #73818f;
   text-transform: uppercase;
   letter-spacing: 0.6px;
   margin-bottom: 6px;
   text-align: center;
`;
SectionLabel.displayName = 'SectionLabel';

const SectionDivider = styled.hr`
   margin-top: ${(p) => (p.$marginTopPx ? p.$marginTopPx + 'px' : '15px')};
   margin-bottom: ${(p) => (p.$marginBottomPx ? p.$marginBottomPx + 'px' : '15px')};
   border: none;
   border-top: 1px solid rgb(220, 220, 220);

   @media (min-width: 2000px) {
      display: none;
   }
`;
SectionDivider.displayName = 'SectionDivider';

const ColumnsWrapper = styled.div`
   display: block;

   @media (min-width: 2000px) {
      display: flex;
      flex-direction: row;
      align-items: stretch;
   }

   @media (min-width: 2300px) {
      max-width: 2300px;
      margin-left: auto;
      margin-right: auto;
   }
`;
ColumnsWrapper.displayName = 'ColumnsWrapper';

const SectionColumn = styled.div`
   min-width: 0;

   @media (min-width: 2000px) {
      flex: 1;
      ${(p) => p.$fixedWidth && `flex: 0 0 ${p.$fixedWidth}; max-width: ${p.$fixedWidth};`}
      ${(p) => p.$paddingX && `padding-left: ${p.$paddingX}; padding-right: ${p.$paddingX};`}
   }
`;
SectionColumn.displayName = 'SectionColumn';

const WideCustomCardBody = styled(CustomCardBody)`
   padding-bottom: 35px;
   @media (min-width: 2000px) {
      max-width: none;
   }
`;
WideCustomCardBody.displayName = 'WideCustomCardBody';

const VerticalDivider = styled.div`
   display: none;
   flex-shrink: 0;
   width: 1px;
   background: rgb(220, 220, 220);
   margin: 0 20px;

   @media (min-width: 2000px) {
      display: block;
   }
`;
VerticalDivider.displayName = 'VerticalDivider';

// Chips and filter-type buttons share one flex row
const FilterRow = styled.div`
   display: flex;
   flex-wrap: wrap;
   align-items: center;
   gap: 4px;
`;
FilterRow.displayName = 'FilterRow';

// Options row: toggle left, Apply button right
const OptionsRow = styled.div`
   display: flex;
   align-items: center;
   justify-content: space-between;
   flex-wrap: wrap;
   gap: 10px;
   margin-top: 4px;
`;
OptionsRow.displayName = 'OptionsRow';

// ─── Toggle switch ────────────────────────────────────────────────────────────

const ToggleWrapper = styled.div`
   display: inline-flex;
   align-items: center;
   gap: 8px;
   cursor: ${(p) => (p.$disabled ? 'default' : 'pointer')};
   user-select: none;
   opacity: ${(p) => (p.$disabled ? 0.5 : 1)};
`;
ToggleWrapper.displayName = 'ToggleWrapper';

const ToggleTrack = styled.span`
   position: relative;
   width: 28px;
   height: 16px;
   border-radius: 8px;
   background: ${(p) => (p.$checked ? '#20a8d8' : '#cfd8dc')};
   flex-shrink: 0;
   transition: background 0.2s;
`;
ToggleTrack.displayName = 'ToggleTrack';

const ToggleThumb = styled.span`
   position: absolute;
   top: 2px;
   left: ${(p) => (p.$checked ? '14px' : '2px')};
   width: 12px;
   height: 12px;
   border-radius: 50%;
   background: #fff;
   box-shadow: 0 1px 3px rgba(0, 0, 0, 0.3);
   transition: left 0.2s;
`;
ToggleThumb.displayName = 'ToggleThumb';

const ConcurrencyLabel = styled.span`
   display: inline-flex;
   align-items: center;
   gap: 5px;
   font-size: 13px;
   color: #73818f;
`;
ConcurrencyLabel.displayName = 'ConcurrencyLabel';

const InfoCircle = styled.span`
   display: inline-flex;
   align-items: center;
   justify-content: center;
   width: 16px;
   height: 16px;
   border-radius: 50%;
   border: 1.5px solid #73818f;
   color: #73818f;
   font-size: 10px;
   font-weight: 700;
   flex-shrink: 0;
   cursor: default;
`;
InfoCircle.displayName = 'InfoCircle';

// ─── DateTimePicker layout override ──────────────────────────────────────────

const DateRangeCompact = styled.div`
   display: flex;
   flex-direction: column;
   align-items: center;
   ${Description} {
      font-size: 9px;
      font-weight: 400;
      color: #73818f;
      text-transform: uppercase;
      letter-spacing: 0.5px;
      margin-bottom: 3px;
      margin-left: 0;
   }
   ${DescriptionSpan} {
      color: #73818f;
      font-size: 9px;
      font-weight: 400;
   }
`;
DateRangeCompact.displayName = 'DateRangeCompact';

const DateTimeRow = styled.div`
   display: flex;
   align-items: flex-end;
   gap: 6px;
`;
DateTimeRow.displayName = 'DateTimeRow';

// ─────────────────────────────────────────────────────────────────────────────

const SETTER_MAP_KEYS = ['streams', 'ips', 'jtis', 'referrers', 'countries', 'providers', 'users', 'tags'];

const MIN_START_DATE = moment.utc('2026-01-23T00:00:00.000Z');

function MainInputs({
   localFilter,
   setStreams,
   setIps,
   setJtis,
   setReferrers,
   setCountries,
   setProviders,
   setUsers,
   setTags,
   start,
   setStart,
   end,
   setEnd,
   hasTableRequestStarted,
   activateTriggerRequest,
   isLoading,
   concurrencyIsDesired,
   setConcurrencyState,
   filteringChanged,
}) {
   const [datePickerHasError, setHasError] = useState(false);
   const [activeTab, setActiveTab] = useState('streams');

   const orgaHash = useSelector(hashSelector);
   const cancelToken = useSelector(cancelTokenSelector);

   // 2026-01-22 has type conflicted fields in ES

   const setterMap = {
      streams: setStreams,
      ips: setIps,
      jtis: setJtis,
      referrers: setReferrers,
      countries: setCountries,
      providers: setProviders,
      users: setUsers,
      tags: setTags,
   };

   function handleTabClick(key) {
      setActiveTab(key);
   }

   function handleAdd(key, val, label) {
      const existing = localFilter[key] ?? [];
      setterMap[key]([...existing, { value: val, label: label ?? val }]);
   }

   function handleRemove(key, val) {
      setterMap[key]((localFilter[key] ?? []).filter((item) => item.value !== val));
   }

   const filterCounts = Object.fromEntries(
      SETTER_MAP_KEYS.map((k) => [k, Array.isArray(localFilter[k]) ? localFilter[k].length : 0]),
   );

   return (
      <WideCustomCardBody $maxWidth={'1000px'}>
         <ColumnsWrapper>
            <SectionColumn $fixedWidth='400px'>
               <DateRangeCompact>
                  <SectionLabel>Time Range</SectionLabel>
                  <DateTimeRow>
                     <DateTimePicker
                        startDate={start}
                        endDate={end}
                        onValidDateChange={({ gte, lt }) => {
                           setStart(gte);
                           setEnd(lt);
                        }}
                        maxInputTimeRange={MAX_DURATION_PER_VIEW[GUARDIAN_VIEW]}
                        setHasError={setHasError}
                        minDate={MIN_START_DATE}
                        hasButtonMargin={false}
                        useErrorLineCollapse={true}
                     />
                  </DateTimeRow>
               </DateRangeCompact>
            </SectionColumn>

            <SectionDivider />
            <VerticalDivider />

            <SectionColumn $paddingX='20px'>
               <SectionLabel>Filter</SectionLabel>
               <FilterTabBar activeTab={activeTab} onTabClick={handleTabClick} filterCounts={filterCounts} />

               <FilterInputPanel
                  activeTab={activeTab}
                  localFilter={localFilter}
                  onAdd={handleAdd}
                  onRemove={handleRemove}
                  orgaHash={orgaHash}
                  cancelToken={cancelToken}
               />

               <FilterChipStrip localFilter={localFilter} onRemove={handleRemove} />
            </SectionColumn>

            <SectionDivider />
            <VerticalDivider />

            <SectionColumn $fixedWidth='400px'>
               <SectionLabel>Max Concurrency</SectionLabel>
               <div style={{ textAlign: 'center' }}>
                  <ToggleWrapper
                     $disabled={hasTableRequestStarted}
                     onClick={() => !hasTableRequestStarted && setConcurrencyState(!concurrencyIsDesired)}
                  >
                     <ToggleTrack $checked={concurrencyIsDesired}>
                        <ToggleThumb $checked={concurrencyIsDesired} />
                     </ToggleTrack>
                     <ConcurrencyLabel>Calculate max concurrency metric (takes more time)</ConcurrencyLabel>
                  </ToggleWrapper>
               </div>
            </SectionColumn>
         </ColumnsWrapper>

         <Collapse isOpen={filteringChanged}>
            <CenterContainer marginTopPx={25} marginBottomPx={10}>
               <ExtButton
                  $widthPx={120}
                  $border={'1px solid rgb(255, 147, 63)'}
                  $bgColor={'rgba(255, 147, 63, 1)'}
                  $bgColorHover={'rgba(255, 147, 63, 0.9)'}
                  $borderHover={'1px solid rgba(255, 147, 63, 0.9)'}
                  $borderRadiusPx={5}
                  color='primary'
                  onClick={() => {
                     activateTriggerRequest(true);
                     if (!isTokenStillValid()) {
                        const { pathname, search } = history.location;
                        clear();
                        history.push(pathname + search);
                     }
                  }}
                  disabled={hasTableRequestStarted || datePickerHasError}
               >
                  {isLoading ? (
                     <BlockSpinner
                        ballSizePx={10}
                        ballColor={'rgb(230, 230, 230)'}
                        paddingTopBottomPx={0}
                        widthPx={41.15}
                     />
                  ) : (
                     <>{'Apply Filters'}</>
                  )}
               </ExtButton>
            </CenterContainer>
         </Collapse>
      </WideCustomCardBody>
   );
}

MainInputs.propTypes = {
   localFilter: PropTypes.object,
   setStreams: PropTypes.func,
   setIps: PropTypes.func,
   setJtis: PropTypes.func,
   setReferrers: PropTypes.func,
   setCountries: PropTypes.func,
   setProviders: PropTypes.func,
   setUsers: PropTypes.func,
   setTags: PropTypes.func,
   start: momentPropTypes.momentObj,
   setStart: PropTypes.func,
   end: momentPropTypes.momentObj,
   setEnd: PropTypes.func,
   hasTableRequestStarted: PropTypes.bool,
   activateTriggerRequest: PropTypes.func,
   isLoading: PropTypes.bool,
   concurrencyIsDesired: PropTypes.bool,
   setConcurrencyState: PropTypes.func,
   filteringChanged: PropTypes.bool,
};

export default MainInputs;
